/**
 * About Section Component
 * Migrated to TypeScript with subcomponents
 */

import React, { useEffect, useRef, useState } from 'react';
import { FaUser, FaMapMarkerAlt, FaGraduationCap, FaBriefcase, FaCode, FaBrain, FaCogs, FaMobile } from 'react-icons/fa';
import { personalInfo } from '../../data/personal';
import { useColorScheme } from '../../contexts/ColorSchemeContext';
import { IStat, ISkillProgress } from './types';

const About: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);
  const { getColorClasses } = useColorScheme();
  const colors = getColorClasses();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const stats: IStat[] = [
    { label: 'Years Experience', value: '3+', icon: FaBriefcase },
    { label: 'Projects Completed', value: '10+', icon: FaUser },
    { label: 'Technologies', value: '25+', icon: FaGraduationCap },
    { label: 'Location', value: 'Austin, TX', icon: FaMapMarkerAlt }
  ];

  const coreSkills: ISkillProgress[] = [
    { skill: 'Software Development', level: 90 },
    { skill: 'Machine Learning', level: 85 },
    { skill: 'DevOps & Cloud', level: 80 },
    { skill: 'Mobile Development', level: 75 }
  ];

  return (
    <section id="about" ref={sectionRef} className="py-20">
      <div className="container mx-auto px-4">
        <div className={`transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          {/* Section Header */}
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              <span className="accent-gradient-text">
                About Me
              </span>
            </h2>
            <p className={`text-xl ${colors.textSecondary} max-w-3xl mx-auto`}>
              Passionate software engineer with expertise in full-stack development, AI/ML, and DevOps
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Column - Bio */}
            <div className="space-y-6">
              <div className={`${colors.cardBackground} rounded-2xl p-8 shadow-lg border ${colors.borderColor}`}>
                <h3 className={`text-2xl font-bold mb-4 ${colors.textPrimary}`}>
                  My Story
                </h3>
                <p className={`${colors.textSecondary} leading-relaxed mb-6`}>
                  {personalInfo.bio}
                </p>
                <p className={`${colors.textSecondary} leading-relaxed`}>
                  I'm passionate about creating innovative solutions that make a real impact. 
                  My journey in technology has taken me from mobile app development to AI/ML research, 
                  and I'm always excited to learn new technologies and tackle challenging problems.
                </p>
              </div>

              {/* Contact Info */}
              <div className={`${colors.cardBackground} rounded-2xl p-6 shadow-lg border ${colors.borderColor}`}>
                <h4 className={`text-lg font-semibold mb-4 ${colors.textPrimary}`}>
                  Quick Info
                </h4>
                <div className="space-y-3">
                  <div className="flex items-center space-x-3">
                    <FaMapMarkerAlt className={`w-5 h-5 ${colors.text}`} />
                    <span className={colors.textSecondary}>{personalInfo.location}</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <FaGraduationCap className={`w-5 h-5 ${colors.text}`} />
                    <span className={colors.textSecondary}>MS Computer Science</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <FaBriefcase className={`w-5 h-5 ${colors.text}`} />
                    <span className={colors.textSecondary}>Software Engineer</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column - Stats */}
            <div className="space-y-6">
                      {/* Stats Grid */}
                      <div className="grid grid-cols-2 gap-4">
                        {stats.map((stat, index) => (
                          <div
                            key={stat.label}
                            className={`${colors.cardBackground} rounded-xl p-4 text-center shadow-lg border ${colors.borderColor} hover:shadow-xl transition-all duration-300 transform hover:scale-105`}
                            style={{ animationDelay: `${index * 0.1}s` }}
                          >
                            <div className="w-10 h-10 mx-auto mb-3 bg-gradient-to-br accent-gradient-bg rounded-full flex items-center justify-center">
                              <stat.icon className="w-5 h-5 text-white" />
                            </div>
                            <div className={`text-2xl font-bold ${colors.textPrimary} mb-1`}>
                              {stat.value}
                            </div>
                            <div className={`text-xs ${colors.textSecondary}`}>
                              {stat.label}
                            </div>
                          </div>
                        ))}
                      </div>

              {/* Skills Preview */}
              <div className={`${colors.cardBackground} rounded-2xl p-6 shadow-lg border ${colors.borderColor}`}>
                <h4 className={`text-lg font-semibold mb-4 ${colors.textPrimary} flex items-center`}>
                  <FaCode className="w-5 h-5 mr-2 text-accent-500" />
                  Core Expertise
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  {coreSkills.map((skill, index) => {
                    const getExpertiseIcon = (skillName: string) => {
                      const iconMap: Record<string, any> = {
                        'Software Development': FaCode,
                        'Machine Learning': FaBrain,
                        'DevOps & Cloud': FaCogs,
                        'Mobile Development': FaMobile
                      };
                      return iconMap[skillName] || FaCode;
                    };
                    
                    const Icon = getExpertiseIcon(skill.skill);
                    
                    return (
                      <div key={skill.skill} className="flex items-center space-x-3 p-3 rounded-lg bg-gray-50 dark:bg-gray-800">
                        <div className="w-8 h-8 bg-gradient-to-br accent-gradient-bg rounded-full flex items-center justify-center">
                          <Icon className="w-4 h-4 text-white" />
                        </div>
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          {skill.skill}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;

